import React, { useState } from 'react';
import { addDoc, collection, doc, getDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { showToast } from '../utils/toast';

function EmailDeliveryTest() {
  const { currentUser } = useAuth();
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [logStatus, setLogStatus] = useState('');
  const [mailStatus, setMailStatus] = useState('');

  const handleSendTestNotification = async () => {
    if (!currentUser) {
      showToast({ message: 'ログインが必要です。', type: 'error' });
      return;
    }

    setSending(true);
    setResult(null);
    setLogStatus('');
    setMailStatus('');

    try {
      const docRef = await addDoc(collection(db, 'notifications'), {
        recipientId: currentUser.uid,
        type: 'new_application',
        isRead: false,
        message: `メール配送テストです。作成時刻: ${new Date().toLocaleString('ja-JP')}`,
        senderName: 'メール配送テスト',
        createdAt: serverTimestamp(),
        testOnly: true,
      });

      const mailId = `notification_${docRef.id}`;
      setResult({
        notificationId: docRef.id,
        mailId,
        recipientId: currentUser.uid,
        recipientEmail: currentUser.email || '(Firebase Auth email なし)',
      });
      showToast({ message: 'テスト通知を作成しました。数秒後に診断ログを確認してください。', type: 'success' });
    } catch (error) {
      console.error('メール配送テスト通知の作成に失敗しました:', error);
      showToast({ message: 'テスト通知の作成に失敗しました。', type: 'error' });
    } finally {
      setSending(false);
    }
  };

  const handleCheckDeliveryLog = async () => {
    if (!result?.notificationId) return;
    setLogStatus('確認中...');
    setMailStatus('確認中...');

    try {
      const logSnap = await getDoc(doc(db, 'notificationDeliveryLogs', result.notificationId));
      if (!logSnap.exists()) {
        setLogStatus('まだ診断ログが作成されていません。5〜10秒待ってから再確認してください。');
        return;
      }

      const data = logSnap.data();
      setLogStatus(JSON.stringify({
        status: data.status || '',
        notificationType: data.notificationType || '',
        mailId: data.mailId || '',
        hasEmail: data.hasEmail ?? null,
        maskedEmail: data.maskedEmail || '',
        errorMessage: data.errorMessage || '',
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate().toLocaleString('ja-JP') : '',
      }, null, 2));
    } catch (error) {
      console.error('通知配送診断ログの取得に失敗しました:', error);
      setLogStatus(
        '診断ログを画面から読めませんでした。admin claim がないユーザーでは正常です。Firebase Console の notificationDeliveryLogs を確認してください。'
      );
    }

    try {
      const callable = httpsCallable(functions, 'getNotificationMailDeliveryStatus');
      const response = await callable({ notificationId: result.notificationId });
      setMailStatus(JSON.stringify(response.data, null, 2));
    } catch (error) {
      console.error('mail 配送状態の取得に失敗しました:', error);
      setMailStatus(`mail 配送状態の取得に失敗しました: ${error.message || error.code || error}`);
    }
  };

  return (
    <div className="container" style={{ maxWidth: 840, margin: '0 auto', padding: '24px 16px' }}>
      <div className="card" style={{ padding: 20 }}>
        <h1>メール配送テスト</h1>
        <p>
          応募・承認フローを通さず、ログイン中ユーザー宛てにテスト通知を作成します。
          `notifications` 作成後、Cloud Functions が `mail/notification_...` を作成できるかを確認します。
        </p>

        <div style={{ marginTop: 16 }}>
          <p><strong>ログインユーザー:</strong> {currentUser?.uid}</p>
          <p><strong>Firebase Auth email:</strong> {currentUser?.email || '(なし)'}</p>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16 }}>
          <button className="btn btn-primary" onClick={handleSendTestNotification} disabled={sending}>
            {sending ? '作成中...' : 'テスト通知を作成する'}
          </button>
          <button className="btn btn-outline" onClick={handleCheckDeliveryLog} disabled={!result?.notificationId}>
            診断ログを確認する
          </button>
        </div>

        {result && (
          <div style={{ marginTop: 20 }}>
            <h2>作成結果</h2>
            <pre style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(result, null, 2)}</pre>
            <p>
              Firebase Console で確認する場合は、`notificationDeliveryLogs/{result.notificationId}` と
              `mail/{result.mailId}` を確認してください。
            </p>
          </div>
        )}

        {logStatus && (
          <div style={{ marginTop: 20 }}>
            <h2>診断ログ</h2>
            <pre style={{ whiteSpace: 'pre-wrap' }}>{logStatus}</pre>
          </div>
        )}

        {mailStatus && (
          <div style={{ marginTop: 20 }}>
            <h2>mail delivery</h2>
            <pre style={{ whiteSpace: 'pre-wrap' }}>{mailStatus}</pre>
          </div>
        )}
      </div>
    </div>
  );
}

export default EmailDeliveryTest;
